import React, { useEffect, useState, useRef, useCallback } from 'react';
import { Outlet, NavLink, useNavigate, useLocation } from 'react-router-dom';
import { toast } from 'sonner';
import { Users, FileText, FolderOpen, BarChart3, AtSign, ListTree, Search, ChevronDown, LogOut, User, Building, Gift, CreditCard, ShieldCheck, MessagesSquare, Megaphone, ScrollText, Ban, ClipboardCheck, CalendarDays, CalendarRange, Zap, TrendingUp, HelpCircle, Link2, LayoutDashboard, Menu, Bot, Crown, Camera , GraduationCap } from 'lucide-react';
import Logo from '../../components/Logo';
import { Avatar, AvatarFallback } from '../../components/ui/avatar';
import { Badge } from '../../components/ui/badge';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, DropdownMenuSeparator, DropdownMenuLabel } from '../../components/ui/dropdown-menu';
import { Button } from '../../components/ui/button';
import { useAuth } from '../../context/AuthContext';
import api from '../../lib/api';
import { chargerComptes } from '../../lib/comptesPartages';
import OnboardingTour, { resetOnboardingTour, EVT_RELANCE_GUIDE } from '../../components/OnboardingTour';
import AttributeFanDialog from '../../components/AttributeFanDialog';
import ConnectionStatus from '../../components/ConnectionStatus';
import TelegramSafetyModal from '../../components/TelegramSafetyModal';
import LanguageToggle from '../../components/LanguageToggle';

// Short, pleasant two-note "ding" played on a new DropFans sale (no audio asset needed).
// Wrapped in try/catch: browsers block audio without prior user interaction — on the
// dashboard the user has always interacted, so it plays; if blocked, it fails silently.
function playSaleSound() {
  try {
    const Ctx = window.AudioContext || window.webkitAudioContext;
    if (!Ctx) return;
    const ctx = new Ctx();
    const now = ctx.currentTime;
    [[880, 0], [1318.5, 0.12]].forEach(([freq, t]) => {
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      osc.type = 'sine';
      osc.frequency.value = freq;
      gain.gain.setValueAtTime(0.0001, now + t);
      gain.gain.exponentialRampToValueAtTime(0.18, now + t + 0.02);
      gain.gain.exponentialRampToValueAtTime(0.0001, now + t + 0.28);
      osc.connect(gain);
      gain.connect(ctx.destination);
      osc.start(now + t);
      osc.stop(now + t + 0.3);
    });
    setTimeout(() => { try { ctx.close(); } catch (e) { /* noop */ } }, 900);
  } catch (e) {
    /* autoplay blocked or Web Audio unsupported — silent */
  }
}

// « Ouvrir le chat » : avec le modèle quand on le connaît (sinon la messagerie cherche dans le modèle affiché).
const lienChat = (accountId, convId) => `/dashboard/messages?${accountId ? `account=${encodeURIComponent(accountId)}&` : ''}conv=${encodeURIComponent(convId)}`;

// Notifications du bot d'accueil (D12). `at` = heure du SERVEUR (UTC, souvent sans « Z ») : c'est elle qui sert de
// `since`, jamais l'heure du PC (un PC en avance ne recevait plus rien).
const KINDS_BOT = ['bot_main', 'bot_vente', 'bot_alerte'];
const NOTIFS_MAX = 10;
const atMs = (at) => new Date(/[zZ]|[+-]\d\d:?\d\d$/.test(String(at)) ? at : `${at}Z`).getTime();
// Trie une réponse de /hera/notifications : celles à afficher (plus anciennes d'abord), les ids à ne plus afficher, le
// `since` suivant = le plus grand `at` traité, et `reste` = d'autres attendent le tour suivant. 1re réponse (rattrapage des
// 12 dernières heures au chargement) : seules les notifications du bot sont reprises. Déjà affichées une fois (ids gardés
// dans le navigateur, D12) : ignorées, même après un rechargement ou dans un autre onglet.
function trierNotifsBot(rows, { vues, dejaVues, rattrapage, since }) {
  const tri = (Array.isArray(rows) ? rows : [])
    .filter((n) => n && n.id && n.at && !Number.isNaN(atMs(n.at)))
    .sort((a, b) => atMs(a.at) - atMs(b.at) || (String(a.at) < String(b.at) ? -1 : String(a.at) > String(b.at) ? 1 : 0));
  const nouvelles = tri.filter((n) => !vues.has(n.id) && !dejaVues[n.id] && (!rattrapage || KINDS_BOT.includes(n.kind)));
  const montrer = nouvelles.slice(0, NOTIFS_MAX);
  // Trop d'un coup : on s'arrête à la dernière affichée, les suivantes reviennent au tour d'après.
  const fin = montrer.length < nouvelles.length ? tri.indexOf(montrer[montrer.length - 1]) : tri.length - 1;
  return {
    montrer,
    ignorer: tri.filter((n) => !nouvelles.includes(n)).map((n) => n.id),
    since: fin >= 0 ? tri[fin].at : since,
    reste: montrer.length < nouvelles.length,
  };
}

const NAV_GROUPS = [
  {
    label: 'Gestion des comptes',
    color: 'text-indigo-400',
    items: [
      { to: '/dashboard', label: 'Tableau de bord', icon: LayoutDashboard, end: true, perm: 'sales_view' },
      { to: '/dashboard/accounts', label: 'Comptes', icon: AtSign, perm: 'manage_telegram', dataTour: 'nav-accounts' },
      { to: '/dashboard/dropfans', label: 'DropFans', icon: Zap, perm: 'dropfans_configure', dataTour: 'nav-dropfans' },
      { to: '/dashboard/tracking-links', label: 'Tracking Links', icon: Link2, perm: 'tracking_view', dataTour: 'nav-tracking' },
      { to: '/dashboard/stories', label: 'Canal', icon: Camera, perm: 'manage_telegram', feature: 'canal', dataTour: 'nav-canal' },
      { to: '/dashboard/lists', label: 'Listes', icon: ListTree, perm: 'lists_view', dataTour: 'nav-lists' },
      { to: '/dashboard/scripts', label: 'Scripts', icon: FileText, perm: 'scripts_view', dataTour: 'nav-scripts' },
      { to: '/dashboard/vault', label: 'Vault', icon: FolderOpen, perm: 'vault_view', dataTour: 'nav-vault' },
      { to: '/dashboard/bot', label: 'Bot Telegram', icon: Bot, perm: 'manage_telegram', feature: 'bot_hera' },
      // E2 : les managers (msg_manage_boxes) voient aussi la page ; modèle et lien restent réservés à manage_telegram.
      { to: '/dashboard/crm-bot', label: "Bot d'accueil", icon: Bot, perm: ['manage_telegram', 'msg_manage_boxes'], feature: 'bot_hera' },
    ],
  },
  {
    label: 'Messages',
    color: 'text-violet-400',
    items: [
      { to: '/dashboard/messages', label: 'Messagerie', icon: MessagesSquare, perm: 'msg_read', unreadBadge: true, dataTour: 'nav-messages' },
      { to: '/dashboard/spenders', label: 'Spenders', icon: Crown, perm: 'sales_view' },
      { to: '/dashboard/my-stats', label: 'Mes stats', icon: TrendingUp, perm: 'msg_read' },
      // 26/09 (Marlon) : « pour le mass dm on va le cacher de tout le monde ».
      // L'ecran n'est plus propose a personne ; la page repond toujours a
      // /dashboard/mass-messages pour qui connait l'adresse.
      // { to: '/dashboard/mass-messages', label: 'Mass DM', icon: Megaphone, perm: 'mass_view' },
      { to: '/dashboard/messages-log', label: 'Journal des messages', icon: ScrollText, perm: 'msg_view_logs' },
      { to: '/dashboard/banned-words', label: 'Mots bannis', icon: Ban, perm: 'banned_words_manage' },
    ],
  },
  {
    label: 'Équipe',
    color: 'text-fuchsia-400',
    items: [
      { to: '/dashboard/employees', label: 'Employés', icon: Users, perm: 'chatters_view', dataTour: 'nav-chatters' },
      { to: '/dashboard/employees/report', label: 'Rapport employé', icon: BarChart3, perm: 'sales_view' },
      { to: '/dashboard/employees/weekly-reports', label: 'Rapports hebdo', icon: FileText, perm: 'sales_view' },
      { to: '/dashboard/whales', label: 'Baleines', icon: Crown, perm: 'sales_view', feature: 'whales', dataTour: 'nav-whales' },
      { to: '/dashboard/employees/accounting', label: 'Ventes', icon: ClipboardCheck, perm: 'sales_view' },
      { to: '/dashboard/employees/planning', label: 'Planning', icon: CalendarDays, perm: 'planning_view_own' },
      { to: '/dashboard/training', label: 'Entraînement', icon: GraduationCap, perm: 'planning_view_own', feature: 'chatter_reports' },
    ],
  },
  {
    label: 'Profil & organisation',
    color: 'text-amber-400',
    items: [
      { to: '/dashboard/profile', label: 'Profil', icon: User },
      // 29/09 (Marlon) : plus d'entrée « Guide » dans le menu — l'écran Profil
      // (« Guides de démarrage ») et le menu de l'avatar sont les seuls chemins.
      { to: '/dashboard/organizations/my-organization', label: 'Mon organisation', icon: Building, perm: 'org_edit' },
      // 26/09 : Marlon garde l'écran Affiliation chez lui — il veut voir ce que ses clients voient.
      { to: '/dashboard/affiliation', label: 'Affiliation', icon: Gift, perm: 'org_edit', dataTour: 'nav-affiliation' },
      { to: '/admin', label: 'Suivi des agences', icon: ShieldCheck, adminSeulement: true },
      { to: '/dashboard/subscriptions', label: 'Abonnement', icon: CreditCard, perm: 'org_edit', dataTour: 'nav-subscription' },
      { to: '/dashboard/organizations/roles', label: 'Rôles & permissions', icon: ShieldCheck, perm: 'org_roles' },
    ],
  },
];

const ALL_ITEMS = NAV_GROUPS.flatMap(g => g.items);

// DIMANCHE OBLIGATOIRE : les chatteurs sans dimanche au planning de base choisissent
// UN dimanche du mois (+ jour/soir). Modal BLOQUANTE tant que ce n'est pas fait.
function SundayDutyGate() {
  const [st, setSt] = useState(null);
  const [date, setDate] = useState('');
  const [moment, setMoment] = useState('jour');
  const [sending, setSending] = useState(false);
  useEffect(() => {
    api.get('/planning/sunday-duty/status').then(({ data }) => setSt(data)).catch(() => {});
  }, []);
  if (!st || !st.concerne || st.soumis) return null;
  const submit = async () => {
    if (!date) { toast.error('Choisis un dimanche'); return; }
    setSending(true);
    try {
      await api.post('/planning/sunday-duty', { date, moment });
      toast.success('Merci ! Ton dimanche est enregistré — il sera ajouté au planning automatiquement.');
      setSt({ ...st, soumis: true });
    } catch (e) { toast.error(e?.response?.data?.detail || 'Échec — réessaie'); }
    finally { setSending(false); }
  };
  const fmt = (iso) => new Date(iso + 'T00:00:00').toLocaleDateString('fr-FR', { weekday: 'long', day: 'numeric', month: 'long' });
  return (
    <div className="fixed inset-0 z-[100] bg-black/80 backdrop-blur-sm flex items-center justify-center p-4">
      <div className="w-full max-w-md rounded-2xl border border-indigo-500/30 bg-[#0C0C10] p-6">
        <div className="text-lg font-bold text-white mb-1">📅 Ton dimanche du mois</div>
        <p className="text-[13px] text-slate-400 mb-4">
          Tu ne travailles pas le dimanche d'habitude — chaque mois, un shift du dimanche est demandé.
          Choisis <b className="text-white">ton dimanche</b> pour {st.mois?.split('-')[1]}/{st.mois?.split('-')[0]} (obligatoire pour continuer).
        </p>
        <div className="space-y-2 mb-4">
          {(st.dimanches || []).map((d) => (
            <label key={d} className={`flex items-center gap-3 rounded-xl border px-3 py-2.5 cursor-pointer transition-colors ${date === d ? 'border-indigo-400/60 bg-indigo-500/10' : 'border-white/10 bg-white/[0.03] hover:bg-white/[0.06]'}`}>
              <input type="radio" name="sunday" checked={date === d} onChange={() => setDate(d)} className="accent-indigo-500" />
              <span className="text-sm text-white capitalize">{fmt(d)}</span>
            </label>
          ))}
        </div>
        <div className="flex gap-2 mb-5">
          {[['jour', '☀️ Shift de jour'], ['soir', '🌙 Shift du soir']].map(([m, label]) => (
            <button key={m} onClick={() => setMoment(m)}
              className={`flex-1 h-10 rounded-xl text-sm font-medium border transition-colors ${moment === m ? 'border-indigo-400/60 bg-indigo-500/15 text-white' : 'border-white/10 bg-white/[0.03] text-slate-300 hover:bg-white/[0.06]'}`}>{label}</button>
          ))}
        </div>
        <Button onClick={submit} disabled={sending || !date} className="w-full bg-indigo-500 hover:bg-indigo-600 h-11">
          {sending ? 'Envoi…' : 'Valider mon dimanche'}
        </Button>
      </div>
    </div>
  );
}

export default function DashboardLayout() {
  const nav = useNavigate();
  const loc = useLocation();
  const { user, agency, logout, hasPerm, hasFeature } = useAuth();
  const [accounts, setAccounts] = useState([]);
  const [chattersCount, setChattersCount] = useState(null);
  const [closedGroups, setClosedGroups] = useState({});
  const [forceNav, setForceNav] = useState(false);
  useEffect(() => { setForceNav(false); }, [loc.pathname]);
  const [tourKey, setTourKey] = useState(0);
  // 29/09 : l'ecran Profil efface le drapeau puis navigue ici. Comme ce layout ne se
  // remonte pas, c'est lui qui doit rejouer le tour quand on le lui demande.
  useEffect(() => {
    const surDemande = (e) => { if (!e.detail || e.detail === 'main') setTourKey(k => k + 1); };
    window.addEventListener(EVT_RELANCE_GUIDE, surDemande);
    return () => window.removeEventListener(EVT_RELANCE_GUIDE, surDemande);
  }, []);
  const [attributeSale, setAttributeSale] = useState(null); // vente « à rattacher » ouverte depuis le popup
  const toggleGroup = (label) => setClosedGroups(s => ({ ...s, [label]: !s[label] }));

  // DropFans new-sale popup — app-wide (not just in Messaging). Backend filters
  // by role: Chatter sees only their assigned models' sales, Manager+ sees all.
  const lastSaleCheckRef = useRef(new Date().toISOString());
  const seenSalesRef = useRef(new Set());
  // Affiche une notif de vente. « à rattacher » = PERSISTANTE (reste jusqu'à ce qu'on
  // la rattache ou la ferme) pour ne plus jamais rater une vente à attribuer.
  const dingSale = useCallback((sale) => {
    if (!sale || seenSalesRef.current.has(sale.id)) return;
    seenSalesRef.current.add(sale.id);
    const amount = (sale.amount || 0).toFixed(2);
    const attached = !!sale.conversation_id;
    const src = sale.source === 'paypal' ? 'PayPal' : 'DropFans';
    toast.success(
      <div className="flex flex-col gap-0.5" data-testid={`new-sale-toast-${sale.id}`}>
        <div className="font-bold text-emerald-300 flex items-center gap-1.5">💰 Vente {src} — {amount} €</div>
        <div className="text-[11px] text-slate-300">{sale.client} · {attached ? (sale.chatter_name || 'attribuée') : <span className="text-amber-300">à rattacher</span>} · {sale.type}</div>
      </div>,
      {
        id: `sale-${sale.id}`,
        duration: attached ? 8000 : Infinity,
        closeButton: !attached,
        action: attached
          ? { label: 'Ouvrir le chat', onClick: () => nav(lienChat(sale.account_id, sale.conversation_id)) }
          : { label: 'Rattacher', onClick: () => setAttributeSale(sale) },
      }
    );
  }, [nav]);
  useEffect(() => {
    // Only users who can see sales (chatters / managers / owner) poll for sale dings.
    // A tracking-only employee has neither perm → skip (avoids 403 spam on /sales).
    if (!hasPerm('msg_read') && !hasPerm('manage_telegram')) return undefined;
    // Au chargement : re-remonter les ventes des 12 dernières heures ENCORE à rattacher,
    // pour qu'une vente non attribuée ne se perde pas (survit à un refresh de page).
    const since12h = new Date(Date.now() - 12 * 3600 * 1000).toISOString();
    api.get('/sales/notifications', { params: { since: since12h } }).then(({ data }) => {
      if (Array.isArray(data)) data.filter(s => !s.conversation_id).slice(0, 10).forEach(dingSale);
    }).catch(() => {});
    const t = setInterval(() => {
      const since = lastSaleCheckRef.current;
      api.get('/sales/notifications', { params: { since } }).then(({ data }) => {
        if (!Array.isArray(data)) return;
        lastSaleCheckRef.current = new Date().toISOString();
        const fresh = data.filter(s => !seenSalesRef.current.has(s.id));
        if (fresh.length > 0) playSaleSound();  // audible "ding" on every new sale
        fresh.slice(0, 5).forEach(dingSale);
      }).catch(() => {});
    }, 20000);
    return () => clearInterval(t);
  }, [dingSale]); // eslint-disable-line react-hooks/exhaustive-deps

  // Notifications de HANDOFF du bot (adressées à : owner + managers + chatteur de service).
  // Persistantes, comme les ventes à rattacher : elles restent à l'écran jusqu'à ce qu'on les ferme. D12 : chacune n'est
  // affichée qu'UNE fois (ni au rechargement, ni dans un autre onglet) ; celles rattrapées au chargement ne sonnent pas.
  const lastHeraCheckRef = useRef(null);  // plus grand `at` reçu du serveur (jamais l'heure du PC)
  const heraRattrapeRef = useRef(false);  // 1re réponse (rattrapage des 12 dernières heures) traitée
  const heraSilenceRef = useRef(true);    // rattrapage du chargement pas fini (il en restait à afficher) : pas de son
  const seenHeraRef = useRef(new Set());
  useEffect(() => {
    if (!hasPerm('msg_read') && !hasPerm('manage_telegram')) return undefined;
    // Ids des notifications déjà affichées, gardés 24 h dans ce navigateur (tous les onglets).
    const cle = `chathub_notifs_bot_vues_${user?.id || 'moi'}`;
    const lireVues = () => {
      try { const o = JSON.parse(localStorage.getItem(cle) || '{}'); return o && typeof o === 'object' ? o : {}; } catch (e) { return {}; }
    };
    const noterVues = (ids) => {
      if (!ids.length) return;
      try {
        const o = lireVues();
        const limite = Date.now() - 24 * 3600 * 1000;
        Object.keys(o).forEach((k) => { if (!(o[k] > limite)) delete o[k]; });
        ids.forEach((nid) => { o[nid] = Date.now(); });
        localStorage.setItem(cle, JSON.stringify(o));
      } catch (e) { /* stockage indisponible : elle pourra revenir au rechargement */ }
    };
    let actif = true;
    let enCours = false;
    let suite = null;          // tour suivant avancé quand il en reste à afficher
    let suiteAttendue = false; // ce tour affiche la suite d'une rafale déjà sonnée
    const sonder = () => {
      if (enCours) return;
      enCours = true;
      // 1er appel : les 12 dernières heures ; ensuite : après le plus grand `at` déjà reçu.
      const since = lastHeraCheckRef.current || new Date(Date.now() - 12 * 3600 * 1000).toISOString();
      api.get('/hera/notifications', { params: { since } }).then(({ data }) => {
        if (!actif || !Array.isArray(data)) return;
        const rattrapage = !heraRattrapeRef.current;
        const r = trierNotifsBot(data, { vues: seenHeraRef.current, dejaVues: lireVues(), rattrapage, since: lastHeraCheckRef.current });
        heraRattrapeRef.current = true;
        lastHeraCheckRef.current = r.since;
        r.ignorer.forEach((nid) => seenHeraRef.current.add(nid));
        noterVues(r.montrer.map((n) => n.id));
        if (r.montrer.length && !heraSilenceRef.current && !suiteAttendue) playSaleSound();
        // Il en reste (plus de 10 d'un coup) : la suite dans 2 s, sans son. Rattrapage du chargement fini : les suivantes sonnent.
        suiteAttendue = r.reste;
        if (r.reste) { clearTimeout(suite); suite = setTimeout(sonder, 2000); } else heraSilenceRef.current = false;
        r.montrer.forEach((n) => {
          seenHeraRef.current.add(n.id);
          const ouvrir = n.conversation_id ? { label: 'Ouvrir le chat', onClick: () => nav(lienChat(n.account_id, n.conversation_id)) } : undefined;
          const qui = `${n.fan_name}${n.model ? ` · ${n.model}` : ''}`;
          if (n.kind === 'bot_alerte') {
            // 22/09 : une alerte du bot pour le patron (ex. : le fan dit avoir trouvé des pages de la modèle ailleurs).
            toast(
              <div className="flex flex-col gap-0.5" data-testid={`notif-bot-alerte-${n.id}`}>
                <div className="font-bold text-rose-300 flex items-center gap-1.5">⚠️ Alerte du bot</div>
                <div className="text-[11px] text-slate-300">{qui} — {n.detail || 'à vérifier'}</div>
              </div>,
              { id: `hera-${n.id}`, duration: Infinity, closeButton: true, action: ouvrir }
            );
            return;
          }
          if (n.kind === 'bot_vente') {
            // Bot d'accueil : vente faite sur le lien du bot (violette, jamais confondue avec une vente d'équipe).
            toast(
              <div className="flex flex-col gap-0.5" data-testid={`notif-bot-vente-${n.id}`}>
                <div className="font-bold text-violet-300 flex items-center gap-1.5">🤖 Vente du bot{n.montant ? ` — ${Number(n.montant).toLocaleString('fr-FR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} €` : ''}</div>
                <div className="text-[11px] text-slate-300">{qui} — {n.detail || 'il a payé, continue la conversation'}</div>
              </div>,
              { id: `hera-${n.id}`, duration: Infinity, closeButton: true, action: ouvrir }
            );
            return;
          }
          toast(
            <div className="flex flex-col gap-0.5" data-testid={`notif-bot-main-${n.id}`}>
              <div className="font-bold text-orange-300 flex items-center gap-1.5">{n.au_shift ? "🔥 Un fan t'attend" : '🔥 Le bot te passe la main'}{n.paid ? ' — le fan a payé' : ''}</div>
              <div className="text-[11px] text-slate-300">
                {qui} — {n.kind === 'bot_main'
                  ? (n.chatteur
                    // Règle A : rendu hors shift → Box du chatteur du prochain shift ; le détail dit pour qui et quand.
                    ? (n.prochain_shift
                      ? <>Box {n.box}{n.detail ? ` · ${n.detail}` : ''}</>
                      : <>→ <b className="text-white">{n.chatteur}</b>{n.box ? ` · Box ${n.box}` : ''}{n.detail ? ` · ${n.detail}` : ''}</>)
                    : (n.detail || 'prends le relais'))
                  : 'prends le relais'}
              </div>
              {/* Seulement quand le bot a envoyé son lien (raison « lien_envoye ») : rendu à la main, bot arrêté ou plus nouveau (3 jours) sans lien, il n'y a rien à remplacer. */}
              {n.kind === 'bot_main' && n.raison === 'lien_envoye' && n.lien_remplace === false && (
                <div className="text-[11px] text-amber-300">⚠ lien pas remplacé : une vente arriverait « à identifier »</div>
              )}
            </div>,
            { id: `hera-${n.id}`, duration: Infinity, closeButton: true, action: ouvrir }
          );
        });
      }).catch(() => {}).finally(() => { enCours = false; });
    };
    sonder();
    const t = setInterval(sonder, 20000);
    return () => { actif = false; clearInterval(t); clearTimeout(suite); };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    // Partagé avec la page ouverte (Tracking links, Logs…) : un seul /accounts quand les deux le demandent ensemble (#146).
    chargerComptes().then(setAccounts).catch(() => {});
    if (hasPerm('chatters_view')) {
      api.get('/chatters').then(({ data }) => setChattersCount(data.length)).catch(() => {});
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ALERTE DISCIPLINE (managers) : un lien de paiement envoyé à un fan qui a dit ne pas
  // pouvoir payer → toast rouge persistant, clic = ouvre la conversation. Pollé (60 s).
  useEffect(() => {
    if (!hasPerm('sales_view')) return undefined;
    const check = () => api.get('/discipline/alerts').then(({ data }) => {
      (data.alerts || []).forEach((a) => {
        api.post(`/discipline/alerts/${a.id}/seen`).catch(() => {});
        // Texte complet — plus aucune coupe qui laissait des phrases inachevées.
        const msg = a.type === 'baleine'
          ? `🐋⚠️ Baleine « ${a.fan} » : ${a.signal_quand}. ${String(a.signal || '')}`
          : a.type === 'coaching'
            ? `🎓 Coaching urgent — ${a.chatter} : ${String(a.signal || '')}`
            : `🚨 ${a.chatter} a envoyé un lien de paiement à ${a.fan} qui a dit (${a.signal_quand}) : « ${String(a.signal || '')} »`;
        const action = a.type === 'coaching'
          ? { label: 'Voir son rapport', onClick: () => nav('/dashboard/employees/weekly-reports') }
          : { label: 'Voir la conv', onClick: () => nav(`/dashboard/messages?account=${a.account_id}&conv=${a.conversation_id}`) };
        toast.error(msg, { id: a.id, duration: Infinity, closeButton: true, action });
      });
    }).catch(() => {});
    check();
    const t = setInterval(check, 60000);
    return () => clearInterval(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // AVERTISSEMENTS PERSONNELS (tous les chatteurs) : lien envoyé à un fan fauché/baleine,
  // erreur baleine attribuée → toast orange persistant adressé au fautif. Pollé (45 s).
  useEffect(() => {
    const check = () => api.get('/discipline/my-alerts').then(({ data }) => {
      (data.alerts || []).forEach((a) => {
        api.post(`/discipline/my-alerts/${a.id}/seen`).catch(() => {});
        toast.warning(`⚠️ ${String(a.signal || '')}`, {
          id: a.id, duration: Infinity, closeButton: true,
          action: a.conversation_id
            ? { label: 'Ouvrir la conv', onClick: () => nav(`/dashboard/messages?account=${a.account_id}&conv=${a.conversation_id}`) }
            : undefined,
        });
      });
    }).catch(() => {});
    check();
    const t = setInterval(check, 45000);
    return () => clearInterval(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const found = ALL_ITEMS.find(n => loc.pathname === n.to) ||
                ALL_ITEMS.slice().sort((a, b) => b.to.length - a.to.length).find(n => !n.end && loc.pathname.startsWith(n.to + '/'));
  const title = found?.label || 'Dashboard';
  const unread = accounts.reduce((s, a) => s + (a.unread || 0), 0);
  const initials = (user?.name || '?').split(' ').map(x => x[0]).join('').slice(0, 2).toUpperCase();

  const signOut = () => { logout(); nav('/auth/login'); };
  const isMessaging = loc.pathname === '/dashboard/messages';
  const showNav = !isMessaging || forceNav;

  return (
    <div className="min-h-screen flex relative" style={{ background: '#06060a' }}>
      <div className="pointer-events-none fixed inset-0 z-0" style={{ background: 'radial-gradient(1200px 560px at 80% -10%, rgba(63,125,255,.13), transparent 60%), radial-gradient(760px 420px at -6% 105%, rgba(63,125,255,.05), transparent 55%)' }} />
      {/* Fond sombre quand le menu-tiroir est ouvert (messagerie sur tout écran ; autres pages sur mobile). */}
      {forceNav && <div className={`fixed inset-0 z-40 bg-black/50 ${isMessaging ? '' : 'md:hidden'}`} onClick={() => setForceNav(false)} aria-hidden />}
      {/* Bouton hamburger : sur la messagerie (tout écran) et sur les autres pages EN MOBILE. */}
      {!forceNav && (
        <button onClick={() => setForceNav(true)} data-testid="open-nav" title="Ouvrir le menu" className={`fixed left-3 bottom-3 z-40 w-11 h-11 rounded-full flex items-center justify-center text-white ${isMessaging ? '' : 'md:hidden'}`} style={{ background: 'linear-gradient(135deg,#3f7dff,#73a6ff)', boxShadow: '0 8px 22px -6px rgba(63,125,255,.6)' }}>
          <Menu size={20} />
        </button>
      )}
      {(showNav || forceNav) && (
      <aside className={`w-[248px] border-r border-white/[0.07] flex-col shrink-0 h-screen z-50 ${(isMessaging || forceNav) ? 'fixed left-0 top-0 flex' : 'sticky top-0 hidden md:flex'}`} style={{ background: (isMessaging || forceNav) ? 'rgba(11,12,18,.96)' : 'rgba(11,12,18,.62)', backdropFilter: 'blur(14px)', WebkitBackdropFilter: 'blur(14px)' }}>
        <button onClick={() => nav('/dashboard')} className="mx-3 mt-3.5 mb-1.5 p-3 rounded-2xl flex items-center gap-3 text-left transition-colors" style={{ background: 'linear-gradient(135deg, rgba(63,125,255,.16), rgba(63,125,255,.03))', border: '1px solid rgba(63,125,255,.22)' }}>
          <span className="w-9 h-9 rounded-xl flex items-center justify-center text-white font-extrabold text-base shrink-0" style={{ background: 'linear-gradient(135deg,#3f7dff,#73a6ff)', boxShadow: '0 6px 18px -4px rgba(63,125,255,.5)' }}>{(agency?.name || 'C')[0].toUpperCase()}</span>
          <div className="flex-1 min-w-0">
            <div className="font-bold text-[13.5px] text-white leading-tight truncate">{agency?.name || 'ChatHub'}</div>
            <div className="text-[11px] text-indigo-300">{agency?.plan ? `Plan ${agency.plan}` : 'ChatHub'} · {accounts.length} comptes</div>
          </div>
          <ChevronDown size={15} className="text-slate-500 shrink-0" />
        </button>
        <nav className="flex-1 px-3 py-3 space-y-4 overflow-y-auto scrollbar-thin">
          {NAV_GROUPS.map(g => {
            // 25/09 : un ecran que le plan ne comprend pas ne s'affiche pas (le serveur
            // le refuse de toute facon en 402).
            const items = g.items.filter(n => (n.action ? true : (Array.isArray(n.perm) ? n.perm.some(p => hasPerm(p)) : hasPerm(n.perm)))
              && hasFeature(n.feature)
              && (!n.adminSeulement || user?.is_super_admin));
            if (items.length === 0) return null;
            const closed = !!closedGroups[g.label];
            return (
              <div key={g.label}>
                <button data-testid={`nav-group-${g.label.toLowerCase().replace(/[^a-z]+/g, '-')}`} onClick={() => toggleGroup(g.label)}
                  className={`w-full flex items-center justify-between text-[10px] uppercase tracking-[.11em] text-slate-600 hover:text-slate-400 font-bold px-2.5 mb-1.5 transition-colors`}>
                  {g.label}
                  <ChevronDown size={13} className={`transition-transform ${closed ? '-rotate-90' : ''} opacity-60`} />
                </button>
                {!closed && (
                <div className="space-y-0.5">
                  {items.map(n => (
                    <NavLink key={n.to} to={n.to} end onClick={() => setForceNav(false)} data-testid={`nav-${n.label.toLowerCase().replace(/[^a-z]+/g, '-')}`} data-tour={n.dataTour}
                      className={({ isActive }) => `group flex items-center gap-3 px-2.5 py-2 rounded-[10px] text-[13px] font-medium transition-all ${isActive ? 'text-white' : 'text-slate-400 hover:text-white hover:bg-white/[0.045]'}`}
                      style={({ isActive }) => isActive ? { background: 'linear-gradient(90deg, rgba(63,125,255,.22), rgba(63,125,255,.05))', boxShadow: 'inset 0 0 0 1px rgba(63,125,255,.3), 0 6px 20px -10px rgba(63,125,255,.4)' } : {}}>
                      {({ isActive }) => (
                        <>
                          <span className={`w-[30px] h-[30px] rounded-[9px] flex items-center justify-center shrink-0 ${isActive ? '' : 'bg-white/5 group-hover:bg-white/[0.08]'}`} style={isActive ? { background: 'rgba(63,125,255,.25)' } : {}}>
                            <n.icon size={16} />
                          </span>
                          <span className="flex-1">{n.label}</span>
                          {n.soon && <span className="text-[9px] font-semibold uppercase tracking-wide text-amber-300 bg-amber-500/15 border border-amber-500/30 rounded-full px-1.5 py-px">Bientôt</span>}
                          {n.unreadBadge && unread > 0 && <span className="text-[10px] font-bold text-white rounded-full px-1.5 py-px" style={{ background: '#3f7dff', boxShadow: '0 0 10px rgba(63,125,255,.5)' }}>{unread}</span>}
                        </>
                      )}
                    </NavLink>
                  ))}
                </div>
                )}
              </div>
            );
          })}
        </nav>
        <div className="p-3 border-t border-white/[0.07] space-y-2.5">
          <DropdownMenu>
            <DropdownMenuTrigger data-testid="user-menu-trigger" className="w-full flex items-center gap-2.5 p-2.5 rounded-xl border border-white/[0.07] bg-white/[0.02] hover:bg-white/[0.04] transition-colors">
              <Avatar className="w-8 h-8"><AvatarFallback className="text-white text-xs" style={{ background: 'linear-gradient(135deg,#3f7dff,#73a6ff)' }}>{initials}</AvatarFallback></Avatar>
              <div className="flex-1 min-w-0 text-left">
                <div className="text-[13px] font-semibold text-white leading-tight truncate" data-testid="user-menu-name">{user?.name}</div>
                <div className="text-[11px] text-slate-500 leading-tight">{user?.role}</div>
              </div>
              <ChevronDown size={15} className="text-slate-500 shrink-0" />
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="bg-[#0e0f17] border-white/10 text-slate-200 w-56">
              <DropdownMenuLabel className="text-slate-400 text-xs">{user?.email}</DropdownMenuLabel>
              <DropdownMenuSeparator className="bg-white/5" />
              <DropdownMenuItem onClick={() => nav('/dashboard/profile')} className="focus:bg-white/5"><User size={14} className="mr-2" /> Profil</DropdownMenuItem>
              {hasPerm('org_edit') && <DropdownMenuItem onClick={() => nav('/dashboard/organizations/my-organization')} className="focus:bg-white/5"><Building size={14} className="mr-2" /> Mon organisation</DropdownMenuItem>}
              {hasPerm('org_edit') && <DropdownMenuItem onClick={() => nav('/dashboard/subscriptions')} className="focus:bg-white/5"><CreditCard size={14} className="mr-2" /> Abonnement</DropdownMenuItem>}
              {hasPerm('manage_telegram') && <DropdownMenuItem onClick={() => { resetOnboardingTour(); setTourKey(k => k + 1); }} className="focus:bg-white/5"><HelpCircle size={14} className="mr-2" /> Relancer le guide</DropdownMenuItem>}
              {hasPerm('org_edit') && <DropdownMenuItem onClick={() => window.open('https://t.me/+HCkfvKKhvJQwYjM0', '_blank')} className="focus:bg-white/5"><MessagesSquare size={14} className="mr-2" /> Support</DropdownMenuItem>}
              {user?.is_super_admin && <DropdownMenuItem onClick={() => nav('/admin')} className="focus:bg-white/5 text-rose-300"><ShieldCheck size={14} className="mr-2" /> Admin</DropdownMenuItem>}
              <DropdownMenuSeparator className="bg-white/5" />
              <DropdownMenuItem data-testid="sign-out-button" onClick={signOut} className="focus:bg-white/5 text-rose-400"><LogOut size={14} className="mr-2" /> Déconnexion</DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </aside>
      )}

      <div className="flex-1 flex flex-col min-w-0 relative z-10">
        <main className="flex-1 overflow-auto scrollbar-thin">
          <Outlet />
        </main>
      </div>
      <SundayDutyGate />
      {/* Dashboard/onboarding tour is owner-only — chatters get the Messaging tour instead
          (this one points at Accounts/DropFans/Vault/Employees nav that chatters don't have). */}
      {/* 29/09 : le tour est monte pour TOUS les roles — sinon le bouton « Guide du tableau
          de bord » du Profil affichait « Guide relancé » et n'ouvrait rien pour un manager
          ou un chatteur, c'est-à-dire exactement les gens que cette carte vise. L'ouverture
          AUTOMATIQUE, elle, reste reservee aux comptes qui gèrent Telegram. */}
      <OnboardingTour key={tourKey} forceOpen={tourKey > 0} autoOuvrir={hasPerm('manage_telegram')} />
      <AttributeFanDialog sale={attributeSale} accounts={accounts} onClose={() => { if (attributeSale) toast.dismiss(`sale-${attributeSale.id}`); setAttributeSale(null); }} />
      <ConnectionStatus />
    </div>
  );
}
